import { useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import type { StatementModel, WorkbenchState } from '../../utils/useStatementModel';
import { centsToDollars, excludeProgramPatch, restoreProgramPatch } from '../../utils/useStatementModel';
import { StatementRow } from './StatementRow';
import { MobileStatementCards } from './MobileStatementCards';

interface StatementTableProps {
  model: StatementModel;
  state: WorkbenchState;
  onChange: (patch: Partial<WorkbenchState>) => void;
}

/**
 * The composed statement: reorderable program rows with drill-down, add-back
 * and adjustment lines, restorable excluded rows, and the live total footer.
 */
export function StatementTable({ model, state, onChange }: StatementTableProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const visibleIds = model.programs.map((p) => p.programId);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = visibleIds.indexOf(String(active.id));
    const to = visibleIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    // The new full visible order becomes the pinned order — export preserves it.
    onChange({ rowOrder: arrayMove(visibleIds, from, to) });
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setOverride(programId: string, pct: number | null) {
    const shareOverrides = { ...state.shareOverrides };
    if (pct === null || pct === state.sharePct) delete shareOverrides[programId];
    else shareOverrides[programId] = pct;
    onChange({ shareOverrides });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      {/* Narrow screens: stacked cards, no horizontal scrolling. */}
      <div className="md:hidden">
        <MobileStatementCards model={model} state={state} onChange={onChange} onShareOverride={setOverride} />
      </div>

      <table className="hidden w-full md:table">
        <thead>
          <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="w-8" />
            <th className="py-2 pr-2 text-left">Program</th>
            <th className="py-2 pr-3 text-right">Rent base</th>
            <th className="w-24 py-2 pr-1 text-right">Share</th>
            <th className="py-2 pr-2 text-right">Rent</th>
            <th className="w-8 print:hidden" />
          </tr>
        </thead>
        <tbody>
          <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
            {model.programs.map((line) => (
              <StatementRow
                key={line.programId}
                line={line}
                expanded={expanded.has(line.programId)}
                onToggleExpand={() => toggleExpand(line.programId)}
                onShareOverride={(pct) => setOverride(line.programId, pct)}
                onExclude={() => onChange(excludeProgramPatch(state, line.programId))}
              />
            ))}
          </SortableContext>

          {(model.addBacks.length > 0 || model.adjustments.length > 0) && (
            <tr>
              <td />
              <td colSpan={5} className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Adjustments
              </td>
            </tr>
          )}
          {model.addBacks.map((a) => (
            <tr key={a.key} className="border-t border-gray-100 text-sm text-gray-600">
              <td />
              <td className="py-1.5 pr-2 italic">{a.label}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">+${centsToDollars(a.amountCents)}</td>
              <td className="py-1.5 pr-1 text-right text-xs tabular-nums text-gray-400">
                {Number((state.sharePct * 100).toFixed(2))}%
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">+${centsToDollars(a.rentCents)}</td>
              <td className="print:hidden" />
            </tr>
          ))}
          {model.adjustments.map((a) => (
            <tr key={a.id} className="border-t border-gray-100 text-sm text-gray-600">
              <td />
              <td className="py-1.5 pr-2 italic">{a.label} (manual)</td>
              <td className={`py-1.5 pr-3 text-right tabular-nums ${a.amountCents < 0 ? 'text-red-700' : ''}`}>
                {a.amountCents < 0 ? '−' : '+'}${centsToDollars(Math.abs(a.amountCents))}
              </td>
              <td className="py-1.5 pr-1 text-right text-xs tabular-nums text-gray-400">
                {Number((state.sharePct * 100).toFixed(2))}%
              </td>
              <td className={`py-1.5 pr-2 text-right tabular-nums ${a.rentCents < 0 ? 'text-red-700' : ''}`}>
                {a.rentCents < 0 ? '−' : '+'}${centsToDollars(Math.abs(a.rentCents))}
              </td>
              <td className="print:hidden" />
            </tr>
          ))}

          {model.excludedPrograms.length > 0 && (
            <>
              <tr>
                <td />
                <td colSpan={5} className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Excluded rows (not in totals)
                </td>
              </tr>
              {model.excludedPrograms.map((line) => (
                <tr key={line.programId} className="border-t border-gray-100 text-sm text-gray-400">
                  <td />
                  <td className="py-1.5 pr-2 line-through">{line.programName}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums line-through">
                    ${centsToDollars(line.baseCents)}
                  </td>
                  <td />
                  <td />
                  <td className="py-1.5 pr-1 text-right print:hidden">
                    <button
                      type="button"
                      onClick={() => onChange(restoreProgramPatch(state, line.programId))}
                      className="text-xs text-emerald-700 hover:underline"
                    >
                      restore
                    </button>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-300 text-sm font-semibold text-gray-900">
            <td />
            <td className="py-2 pr-2">TOTAL</td>
            <td className="py-2 pr-3 text-right tabular-nums">${centsToDollars(model.totals.baseCents)}</td>
            <td />
            <td className="py-2 pr-2 text-right tabular-nums">${centsToDollars(model.totals.rentCents)}</td>
            <td className="print:hidden" />
          </tr>
        </tfoot>
      </table>
    </DndContext>
  );
}
